
/**
 * Ensures that all forms created during the same visit
 * use the EXACT same tracking data, even if the iframe
 * reloads or parameters arrive late.
 */

const TRACKING_SNAPSHOT_KEY = "trackingSnapshot:v1";
const FORM_CREATE_COUNT_KEY = "formCreateCount:v1";
const VISIT_ID_KEY = "visitId:v1";

// Only the params we care about for attribution
const TRACKING_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",

    "first_utm_source",
    "first_utm_medium",
    "first_utm_campaign",
    "first_utm_term",

    "gclid",
    "fbclid",
    "_fbc",
    "_fbp",

    "pysTrafficSource",
    "last_pysTrafficSource",

    "organic_source",
    "first_traffic_source",

    "handl_landing_page",
    "handl_landing_page_base",
    "handl_ref",
    "handl_original_ref",
    "handl_ref_domain",
    "handl_url",
    "handl_url_base",
    "handl_ip",
    "handlID",

    "traffic_source",
];

function isValid(v: string | null) {
    return !!v && v !== "null" && v !== "undefined";
}

function buildSnapshot(params: URLSearchParams): URLSearchParams {
    const snap = new URLSearchParams();

    for (const key of TRACKING_KEYS) {
        const value = params.get(key);
        if (isValid(value)) {
            snap.set(key, value!);
        }
    }

    return snap;
}

/**
 * Returns a stable tracking snapshot for the whole visit.
 * - First call creates it
 * - Subsequent calls reuse it (even across reloads/remounts)
 */
export function getOrCreateTrackingSnapshot(
    mergedParams: URLSearchParams
): URLSearchParams {
    const existing = sessionStorage.getItem(TRACKING_SNAPSHOT_KEY);
    if (existing) {
        return new URLSearchParams(existing);
    }

    const snapshot = buildSnapshot(mergedParams);

    sessionStorage.setItem(TRACKING_SNAPSHOT_KEY, snapshot.toString());
    return snapshot;
}

/**
 * Optional helper if you ever want to reset tracking
 * (e.g. debugging or special flows)
 */
export function clearTrackingSnapshot() {
    sessionStorage.removeItem(TRACKING_SNAPSHOT_KEY);
}


export function registerFormCreationForVisit() {
    const prev = parseInt(
        sessionStorage.getItem(FORM_CREATE_COUNT_KEY) || "0",
        10
    );

    const current = prev + 1;
    sessionStorage.setItem(FORM_CREATE_COUNT_KEY, String(current));

    return {
        creationIndex: current,     // 1 = first, 2 = duplicate, etc.
        isDuplicate: current > 1,
    };
}

export function getOrCreateVisitId(): string {
    let id = sessionStorage.getItem(VISIT_ID_KEY);
    if (id) return id;

    // Safari-safe: randomUUID if available, fallback otherwise
    id =
        (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    sessionStorage.setItem(VISIT_ID_KEY, id);
    return id;
}

